import React, { useEffect } from "react";

import FilledInput from "../forms/inputs/filledInput";
import Bodyv2 from "./bodyv2";
import { DEFAULT_TRANSLATABLE_CONTENT, TranslatableContent } from "./models";
import { ICustomCssModelv1 } from "api/files";
import { PromptTypeEnum } from "modules/smartContent/models";
import { useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

interface ITranslatableContentProps {
    translation: TranslatableContent;
    highlightedText?: string;
    activeLcid?: string;
    activeLanguage: string;
    fetchingAnalyses?: boolean;
    customCss?: ICustomCssModelv1;
    smartContentSuggestion: string;
    postSmartContentEnabled?: boolean;
    showSCDropdownButton?: boolean;
    enableImageGallery?: boolean;
    onBodyFocus?: () => void;
    onFullscreen?: () => void;
    onChangeBody: (newBody: string) => void;
    onChangeTitle: (newTitle: string) => void;
    onChangeSummary: (newSummary: string) => void;
    onShowSmartContentInsert: (highlightedText: string, promptType: PromptTypeEnum) => void;
    clearSmartContentSuggestion?: () => void;
    generateAndOpenSmartContent?: (promptTypeForGeneration: PromptTypeEnum, promptTypeForInsert: PromptTypeEnum, body?: string) => void;
}

interface TextPostContent {
    oldTitle: string,
    oldSummary: string
}

const defaultContentText : TextPostContent = {
    oldTitle:"",
    oldSummary: ""
}

/**
 * Form for translatable content
 * - this is a authoring v2 component
 */
const TranslatableContentEditor: React.FunctionComponent<ITranslatableContentProps> = ({
    translation = DEFAULT_TRANSLATABLE_CONTENT,
    highlightedText = "",
    activeLcid,
    activeLanguage,
    fetchingAnalyses = false,
    customCss,
    smartContentSuggestion,
    postSmartContentEnabled,
    enableImageGallery,
    onBodyFocus,
    onFullscreen,
    onChangeBody,
    onChangeSummary,
    onChangeTitle,
    onShowSmartContentInsert,
    clearSmartContentSuggestion,
    generateAndOpenSmartContent
}) => {
    // destructure values
    const { body, title, description } = translation;

    const maxTitleCount = 100;
    const maxSummaryCount = 300;
    const sanitizedTitle = title.substring(0, maxTitleCount);
    const sanitizedDescription = description.substring(0, maxSummaryCount);

    const changedSinceSaved = useSelector((state: GlobalApplicationState) => state.posts.editor.changedSinceSaved);

    const [contentText, setContentText] = React.useState<TextPostContent>(defaultContentText);
    const [summaryError, setSummaryError] = React.useState<string>("");
    const [titleHasAnyValue, setTitleHasAnyValue] = React.useState<boolean>(title.length <= 0);
    const [summaryHasAnyValue, setSummaryHasAnyValue] = React.useState<boolean>(description.length <= 0);

    //Makes sure that these values are always updated in case of a undo.
    useEffect(() => {
        setTitleHasAnyValue(title.length <= 0);
        setSummaryHasAnyValue(description.length <= 0);
    }, [title, description])

    /**
     * Grab title change and pass to parent change handler
     */
    const onBlurTitle = (event: React.FocusEvent<HTMLInputElement>) => {
        let newTitle = event.target.value as string;
        if (contentText.oldTitle !== newTitle) {
            onChangeTitle(newTitle);
        }
    }

    /**
     * Grab summary change and pass to parent change handler
     */
    const onBlurSummary = (event: React.FocusEvent<HTMLInputElement>) => {
        const newSummary = event.target.value as string;

        setSummaryError("");
        if (contentText.oldSummary !== newSummary) {
            onChangeSummary(newSummary)
        }
    }

    const onPasteSummary = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = event.clipboardData.getData('Text');
        const currentText = event.currentTarget.textContent;

        if(pastedText.length + (currentText?.length ?? 0) > 300) {
            setSummaryError("Cannot paste summary longer than 300 characters.");
        }
    }

    /**
     * Tracks the original value written in title textfield
     */
    const onFocusTitle = (event: React.FocusEvent<HTMLInputElement>) => {
        let oldTitle = event.target.value;
        setContentText({...contentText, oldTitle})
    }

    /**
     * Tracks the original value written in summary textfield
     */
    const onFocusSummary = (event: React.FocusEvent<HTMLInputElement>) => {
        let oldSummary = event.target.value;
        setContentText({...contentText, oldSummary})
    }

    /**
     * Updates the Save As Draft button availability if it's the first time entering text in a field.
     */
    const onKeyPressCheck = (currentValue: string, onChange: (newValue: string) => void) => {
        if(currentValue === "") {
            onChange("");
        }
    }

    return (
        <>
            <FilledInput
                refreshable
                label={"Title"}
                labelId="editor-title-input-header"
                onUndo={(newValue: string | undefined) => onChangeTitle(newValue || "")}
                required
                value={sanitizedTitle}
                characterCount
                maxCount={maxTitleCount}
                inputProps={{
                    placeholder: "Enter a title",
                    onFocus: onFocusTitle,
                    onBlur: onBlurTitle,
                }}
                onKeyDown={(value) => onKeyPressCheck(value, onChangeTitle)}
                setMagicButtonDisabled={(disabled: boolean) => setTitleHasAnyValue(disabled)}
                smartContentButtonDisabled={titleHasAnyValue}
                promptType={PromptTypeEnum.Title}
                postSmartContentEnabled={postSmartContentEnabled}
                onSmartContentGenerate={generateAndOpenSmartContent}
            />
            <FilledInput
                refreshable
                onUndo={(newValue: string | undefined) => onChangeSummary(newValue || "")}
                label={"Summary (optional)"}
                value={sanitizedDescription}
                characterCount
                maxCount={maxSummaryCount}
                errorHelperText={summaryError}
                inputProps={{
                    placeholder: "Give an overview of your post to help people quickly see what it's about.",
                    style: {
                        fontSize: 16
                    },
                    onFocus: onFocusSummary,
                    onBlur: onBlurSummary,
                    onPaste: onPasteSummary
                }}
                onKeyDown={(value) => onKeyPressCheck(value, onChangeSummary)}
                onSmartContentGenerate={generateAndOpenSmartContent}
                setMagicButtonDisabled={(disabled: boolean) => setSummaryHasAnyValue(disabled)}
                smartContentButtonDisabled={summaryHasAnyValue}
                postSmartContentEnabled={postSmartContentEnabled}
                promptType={PromptTypeEnum.Summarize}
            />
            <Bodyv2
                fetchingAnalyses={fetchingAnalyses}
                activeLcid={activeLcid}
                activeLanguage={activeLanguage}
                value={highlightedText || body}
                editorOptions={{ min_height: 670 }}
                onChange={onChangeBody}
                onFocus={onBodyFocus}
                onFullscreen={onFullscreen}
                customCss={customCss}
                onShowSmartContentInsert={onShowSmartContentInsert}
                smartContentSuggestion={smartContentSuggestion}
                clearSmartContentSuggestion={clearSmartContentSuggestion}
                enableSmartContentSummarizeButton={summaryHasAnyValue}
                enableSmartContentTitleButton={titleHasAnyValue}
                postSmartContentEnabled={postSmartContentEnabled}
                generateAndOpenSmartContent={generateAndOpenSmartContent}
                changedSinceSaved={changedSinceSaved}
                enableImageGallery={enableImageGallery}
            />
        </>
    )
}

export default TranslatableContentEditor;
